import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import expendituresService from "../services/expendituresService";
import s3Service from "../services/s3Service";
import api from "../api";
import styles from "../assets/expenseAccountEdit.module.css";

const ExpenditureItemEdit = () => {
  const navigate = useNavigate();
  const { propertyId, expenditureId, id } = useParams(); // Support both route formats
  const actualExpenditureId = expenditureId || id; // Handle /expenditures/:id/edit and /properties/:propertyId/expenditures/items/:expenditureId/edit
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form data
  const [expenditure, setExpenditure] = useState({
    property_id: "",
    type_id: "",
    bank_account_id: "",
    creditor: "",
    ticket_id: "",
    due_date: "",
    amount: "",
    amount_paid: "",
    date: "",
    invoice_number: "",
    purchase_order_number: "",
    description: "",
    file: null,
    confirmation_file: null,
    existing_file: null,
    existing_confirmation_file: null,
  });

  const [errors, setErrors] = useState({});

  // Available data for dropdowns
  const [expenditureCategories, setExpenditureCategories] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [property, setProperty] = useState(null);

  // Date picker state
  const [showDatePicker, setShowDatePicker] = useState({});
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);
  const datePickerRefs = useRef({});
  const dueDatePickerRef = useRef(null);
  
  // Date picker month/year states (matching ExpenseAccountEdit)
  const [dueDatePickerMonth, setDueDatePickerMonth] = useState(new Date().getMonth() + 1);
  const [dueDatePickerYear, setDueDatePickerYear] = useState(new Date().getFullYear());
  const [datePickerMonth, setDatePickerMonth] = useState(new Date().getMonth() + 1);
  const [datePickerYear, setDatePickerYear] = useState(new Date().getFullYear());
  
  // File states (matching ExpenseAccountEdit)
  const [existingFile, setExistingFile] = useState(null);
  const [existingConfirmationFile, setExistingConfirmationFile] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [newConfirmationFile, setNewConfirmationFile] = useState(null);

  // Ticket ID typeahead states
  const [showTicketTypeahead, setShowTicketTypeahead] = useState(false);
  const [ticketTypeaheadResults, setTicketTypeaheadResults] = useState([]);
  const ticketTypeaheadRef = useRef(null);

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, actualExpenditureId]);

  // Click outside handler for date pickers and ticket typeahead
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle due date picker click outside
      if (
        showDueDatePicker &&
        dueDatePickerRef.current &&
        !dueDatePickerRef.current.contains(event.target)
      ) {
        setShowDueDatePicker(false);
      }

      // Handle date paid picker click outside
      Object.keys(showDatePicker).forEach((field) => {
        if (
          showDatePicker[field] &&
          datePickerRefs.current[field] &&
          !datePickerRefs.current[field].contains(event.target)
        ) {
          setShowDatePicker((prev) => ({ ...prev, [field]: false }));
        }
      });

      // Handle ticket typeahead click outside
      if (
        showTicketTypeahead &&
        ticketTypeaheadRef.current &&
        !ticketTypeaheadRef.current.contains(event.target)
      ) {
        setShowTicketTypeahead(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker, showDueDatePicker, showTicketTypeahead]);


  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB"); // dd/mm/yyyy format
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Fetch expenditure data (with relations), categories, types, and bank accounts
      // Note: getProperties() removed - property data now comes from expenditure relations
      const [
        expenditureResponse,
        categoriesResponse,
        typesResponse,
        bankAccountsResponse,
      ] = await Promise.all([
        expendituresService.getExpenditure(actualExpenditureId),
        expendituresService.getExpenditureCategories(),
        expendituresService.getExpenditureTypes(),
        expendituresService.getBankAccounts(),
      ]);

      // Set expenditure data and property from the expenditure's relation data
      if (expenditureResponse && expenditureResponse.success) {
        const expenditureData = expenditureResponse.data;
        // Get propertyId from URL or from expenditure data
        const actualPropertyId = propertyId || expenditureData.property_id;

        // Set property from the expenditure's joined relation data
        if (expenditureData.property) {
          setProperty(expenditureData.property);
        }

        setExpenditure({
          property_id: actualPropertyId,
          property_name: expenditureData.property?.name || "",
          type_id: expenditureData.type_id || "",
          bank_account_id: expenditureData.bank_account_id || "",
          creditor: expenditureData.creditor || "",
          ticket_id: expenditureData.ticket_id || "",
          due_date: expenditureData.due_date
            ? formatDateForDisplay(expenditureData.due_date)
            : "",
          amount: expenditureData.amount || "",
          amount_paid: expenditureData.amount_paid || "",
          date: expenditureData.date
            ? formatDateForDisplay(expenditureData.date)
            : formatDateForDisplay(new Date()),
          invoice_number: expenditureData.invoice_number || "",
          purchase_order_number: expenditureData.purchase_order_number || "",
          description: expenditureData.description || "",
          file: null,
          confirmation_file: null,
          existing_file: expenditureData.file || null,
          existing_confirmation_file: expenditureData.confirmation_file || null,
        });
        
        // Set existing file states
        setExistingFile(expenditureData.file || null);
        setExistingConfirmationFile(expenditureData.confirmation_file || null);
      }

      // Combine categories and types into the expected structure
      if (
        categoriesResponse &&
        categoriesResponse.success &&
        typesResponse &&
        typesResponse.success
      ) {
        const categories = categoriesResponse.data || [];
        const types = typesResponse.data || [];

        // Group types by category
        const categoriesWithTypes = categories.map((category) => ({
          category: category.name,
          items: {},
        }));

        // Add types to their respective categories
        types.forEach((type) => {
          const category = categoriesWithTypes.find(
            (cat) =>
              cat.category ===
              categories.find((c) => c.id === type.category_id)?.name
          );
          if (category) {
            category.items[type.id] = type.name;
          }
        });

        setExpenditureCategories(categoriesWithTypes);
      } else {
        // Mock data for development
        setExpenditureCategories([
          {
            category: "Maintenance",
            items: {
              1: "Plumbing",
              2: "Electrical",
              3: "HVAC",
              4: "General Repairs",
            },
          },
          {
            category: "Utilities",
            items: {
              5: "Electricity",
              6: "Water",
              7: "Gas",
              8: "Internet",
            },
          },
          {
            category: "Insurance",
            items: {
              9: "Property Insurance",
              10: "Liability Insurance",
            },
          },
        ]);
      }

      // Set bank accounts
      if (bankAccountsResponse && bankAccountsResponse.success) {
        const accounts = {};
        (bankAccountsResponse.data || []).forEach((account) => {
          accounts[account.id] = account.name;
        });
        setBankAccounts(accounts);
      } else {
        // Mock data for development
        setBankAccounts({
          1: "Main Account",
          2: "Operating Account",
          3: "Reserve Account",
        });
      }
    } catch (error) {
      toast.error("Failed to load expenditure data");
    } finally {
      setLoading(false);
    }
  };

  const formatDateForAPI = (dateString) => {
    if (!dateString) return "";
    const [day, month, year] = dateString.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  const handleFieldChange = (field, value) => {
    setExpenditure((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Handle ticket ID typeahead search (Laravel: minLength: 3)
    if (field === 'ticket_id') {
      handleTicketSearch(value);
    }
  };

  // Ticket ID typeahead search function (matching Laravel typeaheadInitTicketId)
  const handleTicketSearch = async (searchTerm) => {
    // Laravel requires minLength: 3
    if (!searchTerm || searchTerm.trim().length < 3) {
      setTicketTypeaheadResults([]);
      setShowTicketTypeahead(false);
      return;
    }

    try {
      // Use tickets API endpoint: GET /api/tickets/ajax-search/:query/:propertyId?
      const propertyIdParam = propertyId || expenditure.property_id || '';
      const url = propertyIdParam 
        ? `/tickets/ajax-search/${encodeURIComponent(searchTerm)}/${propertyIdParam}`
        : `/tickets/ajax-search/${encodeURIComponent(searchTerm)}`;
      
      const response = await api.get(url);
      
      if (response.data) {
        // API returns array of {id} objects
        const tickets = Array.isArray(response.data) ? response.data : [];
        setTicketTypeaheadResults(tickets);
        setShowTicketTypeahead(tickets.length > 0);
      } else {
        setTicketTypeaheadResults([]);
        setShowTicketTypeahead(false);
      }
    } catch (error) {
      console.error('Error searching tickets:', error);
      setTicketTypeaheadResults([]);
      setShowTicketTypeahead(false);
    }
  };

  // Handle ticket selection from typeahead
  const handleTicketSelect = (ticket) => {
    // Set ticket_id to the selected ticket ID
    setExpenditure((prev) => ({
      ...prev,
      ticket_id: ticket.id.toString(),
    }));
    setShowTicketTypeahead(false);
    setTicketTypeaheadResults([]);
    
    // Clear error if exists
    if (errors.ticket_id) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.ticket_id;
        return newErrors;
      });
    }
  };

  const handleFileChange = async (field, file) => {
    if (!file) return;

    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    if (file.size > maxSize) {
      toast.error(`File size exceeds 100MB limit. Please choose a smaller file.`);
      // Reset file input
      if (field === 'confirmation_file') {
        const input = document.getElementById('confirmation_file_input');
        if (input) input.value = '';
      }
      return;
    }

    try {
      // Upload file to S3
      // Laravel getDirectoryShort() returns: 'public/expenditures/files/'
      // Actual S3 bucket structure: public/expenditures/files/{filename}
      const fileKey = await s3Service.uploadDocument(file, {
        folder: "public/expenditures/files", // Match Laravel S3 bucket structure
        useLaravelStructure: true, // No timestamp prefix
        onProgress: (progress) => {
          console.log(`${field} upload progress:`, progress);
        }
      });

      // Update state matching ExpenseAccountEdit pattern
      if (field === 'file') {
        setNewFile(file);
        setExpenditure(prev => ({ ...prev, fileKey }));
      } else if (field === 'confirmation_file') {
        setNewConfirmationFile(file);
        setExpenditure(prev => ({ ...prev, confirmationFileKey: fileKey }));
      }

      toast.success(`${field === 'confirmation_file' ? 'Payment confirmation' : 'Invoice'} uploaded successfully!`);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file");
      
      // Reset file input on error
      if (field === 'confirmation_file') {
        const input = document.getElementById('confirmation_file_input');
        if (input) input.value = '';
        setNewConfirmationFile(null);
        setExpenditure(prev => ({ ...prev, confirmationFileKey: null }));
      }
    }
  };

  const handleDownloadFile = async (field) => {
    try {
      // Use expendituresService to get presigned URL and trigger direct download
      // Service handles presigned URL fetch and blob download automatically
      await expendituresService.downloadFile(actualExpenditureId, field);
      toast.success("File download started!");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  // Handle file removal (Laravel route: expenditures.file.remove)
  const handleFileRemove = async (field) => {
    if (!actualExpenditureId) {
      toast.error("Cannot remove file: Expenditure ID not found");
      return;
    }

    // Confirm removal
    const fileType = field === 'file' ? 'invoice' : 'payment confirmation';
    if (!window.confirm(`Are you sure you want to remove the ${fileType} file? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);

      // Call backend API to remove file from S3 and database (matching Laravel removeFile)
      const response = await expendituresService.removeFile(actualExpenditureId, field);

      if (response && response.success) {
        // Update local state (matching ExpenseAccountEdit)
        if (field === 'file') {
          setExistingFile(null);
          setNewFile(null);
          setExpenditure(prev => ({ 
            ...prev, 
            file: null, 
            fileKey: null 
          }));
          // Reset the file input element
          const fileInputs = document.querySelectorAll('input[type="file"]');
          if (fileInputs.length > 0) {
            fileInputs[0].value = '';
          }
        } else if (field === 'confirmation_file') {
          setExistingConfirmationFile(null);
          setNewConfirmationFile(null);
          setExpenditure(prev => ({ 
            ...prev, 
            confirmation_file: null, 
            confirmationFileKey: null 
          }));
          // Reset the confirmation file input element
          const fileInputs = document.querySelectorAll('input[type="file"]');
          if (fileInputs.length > 1) {
            fileInputs[1].value = '';
          }
        }

        toast.success(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} file removed successfully!`);
      } else {
        toast.error(response?.message || `Failed to remove ${fileType} file`);
      }
    } catch (error) {
      console.error(`Error removing ${fileType} file:`, error);
      toast.error(`Failed to remove ${fileType} file. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  // Month names array (shared)
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];

  // Date picker handlers (matching ExpenseAccountEdit)
  const handleDateClick = (field) => {
    if (field === 'due_date') {
      setShowDueDatePicker(!showDueDatePicker);
    } else if (field === 'date') {
      setShowDatePicker((prev) => ({
        ...prev,
        [field]: !prev[field],
      }));
    }
  };

  const selectDate = (field, day, month, year) => {
    const formattedDate = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    handleFieldChange(field, formattedDate);
    
    if (field === 'due_date') {
      setShowDueDatePicker(false);
    } else if (field === 'date') {
      setShowDatePicker((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const generateCalendarDays = (month, year) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
    
    const days = [];
    
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return {
      days,
      monthName: monthNames[month - 1], 
      year: year,
      month: month
    };
  };

  // Navigation handlers for date picker (matching ExpenseAccountEdit)
  const handlePrevMonth = (field) => {
    if (field === 'due_date') {
      if (dueDatePickerMonth === 1) {
        setDueDatePickerMonth(12);
        setDueDatePickerYear(dueDatePickerYear - 1);
      } else {
        setDueDatePickerMonth(dueDatePickerMonth - 1);
      }
    } else if (field === 'date') {
      if (datePickerMonth === 1) {
        setDatePickerMonth(12);
        setDatePickerYear(datePickerYear - 1);
      } else {
        setDatePickerMonth(datePickerMonth - 1);
      }
    }
  };

  const handleNextMonth = (field) => {
    if (field === 'due_date') {
      if (dueDatePickerMonth === 12) {
        setDueDatePickerMonth(1);
        setDueDatePickerYear(dueDatePickerYear + 1);
      } else {
        setDueDatePickerMonth(dueDatePickerMonth + 1);
      }
    } else if (field === 'date') {
      if (datePickerMonth === 12) {
        setDatePickerMonth(1);
        setDatePickerYear(datePickerYear + 1);
      } else {
        setDatePickerMonth(datePickerMonth + 1);
      }
    }
  };

  const handleMonthChange = (field, month) => {
    if (field === 'due_date') {
      setDueDatePickerMonth(parseInt(month));
    } else if (field === 'date') {
      setDatePickerMonth(parseInt(month));
    }
  };

  const handleYearChange = (field, year) => {
    if (field === 'due_date') {
      setDueDatePickerYear(parseInt(year));
    } else if (field === 'date') {
      setDatePickerYear(parseInt(year));
    }
  };

  // Generate year options (current year ± 10 years)
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 10; i <= currentYear + 10; i++) {
    years.push(i);
  }

  const dueDatePickerCalendar = generateCalendarDays(dueDatePickerMonth, dueDatePickerYear);
  const datePickerCalendar = generateCalendarDays(datePickerMonth, datePickerYear);

  const validateForm = () => {
    const newErrors = {};

    if (!expenditure.type_id) {
      newErrors.type_id = "Type is required";
    }
    if (!expenditure.creditor) {
      newErrors.creditor = "Creditor is required";
    }
    if (!expenditure.due_date) {
      newErrors.due_date = "Due date is required";
    }
    if (!expenditure.amount) {
      newErrors.amount = "Amount is required";
    }
    if (!expenditure.amount_paid) {
      newErrors.amount_paid = "Amount paid is required";
    }
    if (!expenditure.date) {
      newErrors.date = "Date paid is required";
    }
    if (!expenditure.invoice_number) {
      newErrors.invoice_number = "Invoice number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors below");
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("property_id", expenditure.property_id);
      formData.append("type_id", expenditure.type_id);
      formData.append("bank_account_id", expenditure.bank_account_id || "");
      formData.append("creditor", expenditure.creditor);
      formData.append("ticket_id", expenditure.ticket_id || "");
      formData.append("due_date", formatDateForAPI(expenditure.due_date));
      formData.append("amount", expenditure.amount);
      formData.append("amount_paid", expenditure.amount_paid);
      formData.append("date", formatDateForAPI(expenditure.date));
      formData.append("invoice_number", expenditure.invoice_number);
      formData.append(
        "purchase_order_number",
        expenditure.purchase_order_number || ""
      );
      formData.append("description", expenditure.description || "");

      // Add S3 file keys if available
      if (expenditure.fileKey) {
        formData.append("fileKey", expenditure.fileKey);
      }
      if (expenditure.confirmationFileKey) {
        formData.append("confirmationFileKey", expenditure.confirmationFileKey);
      }

      // Use expenditures.update endpoint (matching Laravel route: expenditures.update)
      // Laravel: PUT /expenditures/{id} → ExpendituresController@update
      const response = await expendituresService.updateExpenditure(
        actualExpenditureId,
        formData
      );

      if (response && response.success) {
        toast.success(response.message || "Expenditure updated successfully!");
        // Navigate immediately to trigger data reload
        navigate('/expenditures', { replace: true });
      } else {
        toast.error(response?.message || "Failed to update expenditure");
      }
    } catch (error) {
      console.error("Error updating expenditure:", error);
      toast.error("Failed to update expenditure");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !expenditure.property_id) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading expenditure data...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/dashboard">Dashboard</Link>
        <span className={styles.separator}>/</span>
        <Link to="/expenditures">Expenditures</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.currentPage}>Edit</span>
      </div>

      <div className={styles.mainSection}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={styles.panel}>
              <div className={styles.panelHeading}>
                <h3>Edit Expenditure</h3>
              </div>
              <div className={styles.panelBody}>
                <form onSubmit={handleSubmit}>
                {/* Type */}
                <div className={styles.formGroup}>
                  <label
                    style={{ textAlign: "right" }}
                    className={`${styles.controlLabel} ${styles.colSm3} ${styles.labelRequired}`}
                  >
                    Type
                  </label>
                  <div className={styles.colSm5}>
                    <select
                      name="type_id"
                      className={styles.formControl}
                      value={expenditure.type_id}
                      onChange={(e) =>
                        handleFieldChange("type_id", e.target.value)
                      }
                      required
                    >
                      <option value="">Please select</option>
                      {expenditureCategories.map((category, catIndex) => (
                        <React.Fragment key={catIndex}>
                          <option disabled>{category.category}</option>
                          {Object.entries(category.items || {}).map(
                            ([id, name]) => (
                              <option key={id} value={id}>
                                {name}
                              </option>
                            )
                          )}
                        </React.Fragment>
                      ))}
                    </select>
                    {errors.type_id && (
                      <div className={styles.textDanger}>{errors.type_id}</div>
                    )}
                  </div>
                </div>

                  {/* Bank Account Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>GG Bank Account</label>
                    <div className={styles.colSm5}>
                      <select
                        className={styles.formControl}
                        value={expenditure.bank_account_id || ""}
                        onChange={(e) => handleFieldChange('bank_account_id', e.target.value)}
                      >
                        <option value="">Please Select</option>
                        {Object.entries(bankAccounts).map(([id, name]) => (
                          <option key={id} value={id}>
                            {name}
                          </option>
                        ))}
                      </select>
                      {errors.bank_account_id && (
                        <div className={styles.errorMessage}>{errors.bank_account_id}</div>
                      )}
                    </div>
                  </div>

                  {/* Company Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Company</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        required
                        value={expenditure.creditor || ""}
                        onChange={(e) => handleFieldChange('creditor', e.target.value)}
                      />
                      {errors.creditor && (
                        <div className={styles.errorMessage}>{errors.creditor}</div>
                      )}
                    </div>
                  </div>

                  {/* Ticket ID Field with Typeahead */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Ticket Id</label>
                    <div className={styles.colSm5}>
                      <div className={styles.typeaheadContainer} ref={ticketTypeaheadRef} style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className={`${styles.formControl} ${styles.jsTicketIdFieldTypeahead}`}
                          maxLength={9}
                          placeholder="Type at least 3 characters"
                          value={expenditure.ticket_id || ""}
                          onChange={(e) => handleFieldChange('ticket_id', e.target.value)}
                          onFocus={() => {
                            // Show typeahead if there are results and input has 3+ characters
                            if (expenditure.ticket_id && expenditure.ticket_id.length >= 3 && ticketTypeaheadResults.length > 0) {
                              setShowTicketTypeahead(true);
                            }
                          }}
                          autoComplete="off"
                        />
                        {showTicketTypeahead && ticketTypeaheadResults.length > 0 && (
                          <div className={styles.typeaheadDropdown}>
                            {ticketTypeaheadResults.map((ticket) => (
                              <div
                                key={ticket.id}
                                className={styles.typeaheadItem}
                                onClick={() => handleTicketSelect(ticket)}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                              >
                                <strong>{ticket.id}</strong>
                              </div>
                            ))}
                          </div>
                        )}
                        {showTicketTypeahead && ticketTypeaheadResults.length === 0 && expenditure.ticket_id && expenditure.ticket_id.length >= 3 && (
                          <div className={styles.typeaheadDropdown}>
                            <div className={styles.typeaheadItem} style={{ color: '#999', cursor: 'default' }}>
                              <strong>No such Ticket</strong>
                            </div>
                          </div>
                        )}
                      </div>
                      {errors.ticket_id && (
                        <div className={styles.errorMessage}>{errors.ticket_id}</div>
                      )}
                    </div>
                  </div>

                  {/* Due Date Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Due Date</label>
                    <div className={styles.colSm2}>
                      <div className={styles.datePickerContainer} ref={dueDatePickerRef}>
                        <input
                          type="text"
                          className={`${styles.formControl} ${styles.jsDatepicker}`}
                          required
                          placeholder="DD/MM/YYYY"
                          value={expenditure.due_date || ""}
                          onChange={(e) => handleFieldChange('due_date', e.target.value)}
                          onClick={() => handleDateClick('due_date')}
                          readOnly
                        />
                        {showDueDatePicker && (
                          <div className={styles.datePickerDropdown}>
                            <div className={styles.datePickerHeader}>
                              <button
                                type="button"
                                className={styles.datePickerNavButton}
                                onClick={() => handlePrevMonth('due_date')}
                                aria-label="Previous month"
                              >
                                ‹
                              </button>
                              <div className={styles.datePickerMonthYear}>
                                <select
                                  className={styles.datePickerSelect}
                                  value={dueDatePickerMonth}
                                  onChange={(e) => handleMonthChange('due_date', e.target.value)}
                                >
                                  {monthNames.map((month, index) => (
                                    <option key={index} value={index + 1}>
                                      {month}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className={styles.datePickerSelect}
                                  value={dueDatePickerYear}
                                  onChange={(e) => handleYearChange('due_date', e.target.value)}
                                >
                                  {years.map((year) => (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <button
                                type="button"
                                className={styles.datePickerNavButton}
                                onClick={() => handleNextMonth('due_date')}
                                aria-label="Next month"
                              >
                                ›
                              </button>
                            </div>
                            <div className={styles.datePickerGrid}>
                              <div className={styles.datePickerWeekdays}>
                                <span>Sun</span>
                                <span>Mon</span>
                                <span>Tue</span>
                                <span>Wed</span>
                                <span>Thu</span>
                                <span>Fri</span>
                                <span>Sat</span>
                              </div>
                              <div className={styles.datePickerDays}>
                                {dueDatePickerCalendar.days.map((day, dayIndex) => (
                                  <span
                                    key={dayIndex}
                                    className={`${styles.datePickerDay} ${day ? styles.available : styles.empty}`}
                                    onClick={() => day && selectDate('due_date', day, dueDatePickerCalendar.month, dueDatePickerCalendar.year)}
                                  >
                                    {day}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {errors.due_date && (
                        <div className={styles.errorMessage}>{errors.due_date}</div>
                      )}
                    </div>
                  </div>

                  {/* Amount Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Amount</label>
                    <div className={styles.colSm5}>
                      <input
                        type="number"
                        step="0.01"
                        className={styles.formControl}
                        required
                        value={expenditure.amount || ""}
                        onChange={(e) => handleFieldChange('amount', e.target.value)}
                      />
                      {errors.amount && (
                        <div className={styles.errorMessage}>{errors.amount}</div>
                      )}
                    </div>
                  </div>

                  {/* Amount Paid Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Amount Paid</label>
                    <div className={styles.colSm5}>
                      <input
                        type="number"
                        step="0.01"
                        className={styles.formControl}
                        required
                        value={expenditure.amount_paid || ""}
                        onChange={(e) => handleFieldChange('amount_paid', e.target.value)}
                      />
                      {errors.amount_paid && (
                        <div className={styles.errorMessage}>{errors.amount_paid}</div>
                      )}
                    </div>
                  </div>

                  {/* Date Paid Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Date Paid</label>
                    <div className={styles.colSm2}>
                      <div className={styles.datePickerContainer} ref={(el) => (datePickerRefs.current["date"] = el)}>
                        <input
                          type="text"
                          className={`${styles.formControl} ${styles.jsDatepicker}`}
                          required
                          placeholder="DD/MM/YYYY"
                          value={expenditure.date || ""}
                          onChange={(e) => handleFieldChange('date', e.target.value)}
                          onClick={() => handleDateClick('date')}
                          readOnly
                        />
                        {showDatePicker["date"] && (
                          <div className={styles.datePickerDropdown}>
                            <div className={styles.datePickerHeader}>
                              <button
                                type="button"
                                className={styles.datePickerNavButton}
                                onClick={() => handlePrevMonth('date')}
                                aria-label="Previous month"
                              >
                                ‹
                              </button>
                              <div className={styles.datePickerMonthYear}>
                                <select
                                  className={styles.datePickerSelect}
                                  value={datePickerMonth}
                                  onChange={(e) => handleMonthChange('date', e.target.value)}
                                >
                                  {monthNames.map((month, index) => (
                                    <option key={index} value={index + 1}>
                                      {month}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  className={styles.datePickerSelect}
                                  value={datePickerYear}
                                  onChange={(e) => handleYearChange('date', e.target.value)}
                                >
                                  {years.map((year) => (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <button
                                type="button"
                                className={styles.datePickerNavButton}
                                onClick={() => handleNextMonth('date')}
                                aria-label="Next month"
                              >
                                ›
                              </button>
                            </div>
                            <div className={styles.datePickerGrid}>
                              <div className={styles.datePickerWeekdays}>
                                <span>Sun</span>
                                <span>Mon</span>
                                <span>Tue</span>
                                <span>Wed</span>
                                <span>Thu</span>
                                <span>Fri</span>
                                <span>Sat</span>
                              </div>
                              <div className={styles.datePickerDays}>
                                {datePickerCalendar.days.map((day, dayIndex) => (
                                  <span
                                    key={dayIndex}
                                    className={`${styles.datePickerDay} ${day ? styles.available : styles.empty}`}
                                    onClick={() => day && selectDate('date', day, datePickerCalendar.month, datePickerCalendar.year)}
                                  >
                                    {day}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {errors.date && (
                        <div className={styles.errorMessage}>{errors.date}</div>
                      )}
                    </div>
                  </div>

                  {/* Invoice Number Field */}
                  <div className={styles.formGroup}>
                    <label className={`${styles.controlLabel} ${styles.labelRequired}`}>Invoice number</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        required
                        value={expenditure.invoice_number || ""}
                        onChange={(e) => handleFieldChange('invoice_number', e.target.value)}
                      />
                      {errors.invoice_number && (
                        <div className={styles.errorMessage}>{errors.invoice_number}</div>
                      )}
                    </div>
                  </div>

                  {/* Purchase Order Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Purchase Order</label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        className={styles.formControl}
                        value={expenditure.purchase_order_number || ""}
                        onChange={(e) => handleFieldChange('purchase_order_number', e.target.value)}
                      />
                      {errors.purchase_order_number && (
                        <div className={styles.errorMessage}>{errors.purchase_order_number}</div>
                      )}
                    </div>
                  </div>

                  {/* Description Field */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Description</label>
                    <div className={styles.colSm5}>
                      <textarea
                        className={styles.formControl}
                        rows={3}
                        value={expenditure.description || ""}
                        onChange={(e) => handleFieldChange('description', e.target.value)}
                      />
                      {errors.description && (
                        <div className={styles.errorMessage}>{errors.description}</div>
                      )}
                    </div>
                  </div>

                  {/* File Upload Section */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Invoice</label>
                    <div className={styles.colSm5}>
                      {existingFile && !newFile ? (
                        <div className={styles.existingFile}>
                          <span>{existingFile}</span>
                          <div className={styles.fileUploadActions}>
                            <button
                              type="button"
                              className={styles.btnWarning}
                              onClick={() => handleDownloadFile('file')}
                            >
                              Download
                            </button>
                            <button
                              type="button"
                              className={styles.btnDanger}
                              onClick={() => handleFileRemove('file')}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.fileUploadContainer}>
                          <div className={styles.fileUploadRow}>
                            <input
                              type="file"
                              className={styles.formControl}
                              onChange={(e) => handleFileChange('file', e.target.files[0])}
                            />
                            {newFile && (
                              <button
                                type="button"
                                className={styles.btnDanger}
                                onClick={() => {
                                  setNewFile(null);
                                  setExpenditure(prev => ({ ...prev, fileKey: null }));
                                  const fileInputs = document.querySelectorAll('input[type="file"]');
                                  if (fileInputs.length > 0) {
                                    fileInputs[0].value = '';
                                  }
                                }}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                          <div className={styles.fileInstructions}>
                            The file can not be larger than 100MB. If you don't want to change file - just leave the field empty.
                          </div>
                        </div>
                      )}
                      {errors.file && (
                        <div className={styles.errorMessage}>{errors.file}</div>
                      )}
                    </div>
                  </div>

                  {/* Confirmation File Upload Section */}
                  <div className={styles.formGroup}>
                    <label className={styles.controlLabel}>Payment Confirmation</label>
                    <div className={styles.colSm5}>
                      {existingConfirmationFile && !newConfirmationFile ? (
                        <div className={styles.existingFile}>
                          <span>{existingConfirmationFile}</span>
                          <div className={styles.fileUploadActions}>
                            <button
                              type="button"
                              className={styles.btnWarning}
                              onClick={() => handleDownloadFile('confirmation_file')}
                            >
                              Download
                            </button>
                            <button
                              type="button"
                              className={styles.btnDanger}
                              onClick={() => handleFileRemove('confirmation_file')}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.fileUploadContainer}>
                          <div className={styles.fileUploadRow}>
                            <input
                              type="file"
                              id="confirmation_file_input"
                              className={styles.formControl}
                              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                              disabled={loading}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleFileChange('confirmation_file', file);
                                }
                              }}
                            />
                            {newConfirmationFile && (
                              <div className={styles.uploadedFileInfo}>
                                <span>{newConfirmationFile.name}</span>
                                <button
                                  type="button"
                                  className={styles.btnDanger}
                                  onClick={() => {
                                    setNewConfirmationFile(null);
                                    setExpenditure(prev => ({ ...prev, confirmationFileKey: null }));
                                    const input = document.getElementById('confirmation_file_input');
                                    if (input) input.value = '';
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                          <div className={styles.fileInstructions}>
                            The file can not be larger than 100MB. If you don't want to change file - just leave the field empty.
                          </div>
                        </div>
                      )}
                      {errors.confirmation_file && (
                        <div className={styles.errorMessage}>{errors.confirmation_file}</div>
                      )}
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className={styles.formGroup} style={{display:'flex', justifyContent:'flex-end'}}>
                    <div className={styles.textRight}>
                      <button
                        style={{background:'#17264B'}}
                        type="submit"
                        className={styles.btnHead}
                        disabled={loading || submitting}
                      >
                        {loading || submitting ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenditureItemEdit;
